// ============================================
// Header Component
// ============================================

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { ROLE_LABELS } from '../../types';
import { Bell } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuRef]);

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : '?';

  const handleSignOut = () => {
    setShowMenu(false);
    logout();
  };

  return (
    <header className="app-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <h1 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
          {user ? ROLE_LABELS[user.role] : ''} Dashboard
        </h1>
      </div>

      {user?.role !== 'architect' && (
        <div style={{ flex: 1, textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', padding: '0 var(--space-4)' }}>
          Developed by AbdelRahman Soltan -- For any bugs please reach out at{' '}
          <a href="mailto:v-asoltaneid@microsoft.com" style={{ color: 'inherit', textDecoration: 'underline' }}>
            v-asoltaneid@microsoft.com
          </a>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        {/* Notification Bell */}
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '44px', minHeight: '44px' }}>
          <Bell size={24} style={{ color: '#ff2a2a', filter: 'drop-shadow(0 0 8px rgba(255, 42, 42, 0.8))' }} />
        </button>

        {/* User Menu - Always visible */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className="glass-avatar"
            onClick={() => setShowMenu(!showMenu)}
            style={{ 
              cursor: 'pointer',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {initials}
          </button>

          {showMenu && (
            <div
              className="glass-card animate-fade-in user-menu-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                width: '220px',
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                marginTop: 'var(--space-2)',
                padding: 'var(--space-3)',
                zIndex: 9999,
              }}
            >
              <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid rgba(139,0,0,0.2)', marginBottom: 'var(--space-2)' }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {user?.email}
                </div>
                <div className="glass-badge glass-badge--open" style={{ marginTop: 'var(--space-2)' }}>
                  {user ? ROLE_LABELS[user.role] : ''}
                </div>
              </div>
               <button
                  className="glass-sidebar__item"
                  onClick={handleSignOut}
                  style={{ 
                    width: '100%', 
                    color: 'var(--error)',
                    padding: 'var(--space-4)',
                    minHeight: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 'var(--space-3)'
                  }}
                >
                  <span className="glass-sidebar__item-icon">🚪</span>
                  <span className="glass-sidebar__item-text">Sign Out</span>
                </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
